import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Divider, 
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import InfoIcon from '@mui/icons-material/Info';
import Layout from '../components/Layout';
import TagCloud from '../components/TagCloud';
import RelatedTags from '../components/RelatedTags';
import TagSuggestions from '../components/TagSuggestions';
import networkStatus from '../services/networkStatus';
import offlineApi from '../services/offlineApi';

/**
 * TagDashboard component that demonstrates all tag features in one place
 */
const TagDashboard = () => {
  const [selectedTag, setSelectedTag] = useState('');
  const [searchedTag, setSearchedTag] = useState(''); // Tag that was actually searched
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [demoTags, setDemoTags] = useState('');
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [offlineTags, setOfflineTags] = useState([]);
  
  // Check offline status when component mounts
  useEffect(() => {
    const checkOfflineStatus = async () => {
      const isOffline = await networkStatus.shouldUseOfflineMode();
      setIsOfflineMode(isOffline);
      
      if (isOffline) {
        // If offline, load tags from offline storage
        loadOfflineTags();
      }
    };
    
    checkOfflineStatus();
    
    // Add listener for online/offline events
    const offlineHandler = () => setIsOfflineMode(true);
    const onlineHandler = () => setIsOfflineMode(false);
    
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('online', onlineHandler);
    
    return () => {
      window.removeEventListener('offline', offlineHandler);
      window.removeEventListener('online', onlineHandler);
    };
  }, []);
  
  // Load tags from offline API
  const loadOfflineTags = async () => {
    try {
      // Get all items from offline API
      const items = await offlineApi.getAllItems();
      
      // Extract all tags from items
      const allTags = {};
      
      items.forEach(item => {
        if (item.custom_tags) {
          // Handle both string and array formats
          const tags = Array.isArray(item.custom_tags) 
            ? item.custom_tags 
            : item.custom_tags.split(',').map(tag => tag.trim()).filter(Boolean);
          
          // Count occurrences of each tag
          tags.forEach(tag => {
            if (!tag) return;
            const normalizedTag = tag.toLowerCase();
            allTags[normalizedTag] = (allTags[normalizedTag] || 0) + 1;
          });
        }
      });
      
      // Convert to array format expected by TagCloud
      const tagsArray = Object.entries(allTags).map(([tag, count]) => ({
        text: tag,
        value: count
      }));
      
      setOfflineTags(tagsArray);
    } catch (error) {
      console.error('Error loading offline tags:', error);
    }
  };


  const handleAddTag = (tag) => {
    // Add tag to existing tags if not already present
    const currentTags = demoTags.split(',').map(t => t.trim()).filter(t => t);
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag].join(',');
      setDemoTags(newTags);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h4" component="h1">
              Tag Dashboard
            </Typography>
            
            {/* Offline indicator */}
            {isOfflineMode && (
              <Chip 
                icon={<CloudOffIcon />} 
                label="Offline Mode" 
                color="warning" 
                variant="outlined"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Explore and manage your tags with these advanced features
          </Typography>
          
          {/* Offline mode alert */}
          {isOfflineMode && (
            <Alert 
              severity="info" 
              icon={<InfoIcon />}
              sx={{ mb: 3 }}
            >
              You're in offline mode. Tag suggestions and related tags may be limited to locally stored data.
              Some features might not be fully functional until you're back online.
            </Alert>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Tag Cloud */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Tag Cloud
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Visualize all your tags based on popularity. Click any tag to see related tags.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {isOfflineMode ? (
                offlineTags.length > 0 ? (
                  <TagCloud tags={offlineTags} maxTags={50} />
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No tags available in offline storage.
                    Tags will appear here once you've added items with tags while offline.
                  </Typography>
                )
              ) : (
                <TagCloud maxTags={50} />
              )}
            </Paper>
          </Grid>

          {/* Tag Suggestions Demo */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Tag Suggestions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enter an item name and description to get tag suggestions based on your content and usage patterns.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField 
                fullWidth
                label="Item Name"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                margin="normal"
              />
              
              <TextField 
                fullWidth
                label="Item Description"
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                multiline
                rows={3}
                margin="normal"
              />
              
              <TextField 
                fullWidth
                label="Tags"
                value={demoTags}
                onChange={(e) => setDemoTags(e.target.value)}
                margin="normal"
                helperText="Click a suggestion below to add it to your tags"
              />
              
              <Box sx={{ mt: 2, mb: 2 }}>
                {isOfflineMode ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Tag suggestions require online access to the AI service.
                  You can still manually add tags while offline.
                </Typography>
              ) : (
                <TagSuggestions 
                  itemName={itemName}
                  itemDescription={itemDescription}
                  onAddTag={handleAddTag}
                />
              )}
              </Box>
            </Paper>
          </Grid>

          {/* Related Tags Demo */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Related Tags
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Click a tag in the cloud above or enter a tag below to see other tags that are frequently used together.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TextField 
                  label="Enter a tag"
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  sx={{ flex: 1 }}
                />
                
                <Button 
                  variant="contained" 
                  onClick={() => setSearchedTag(selectedTag.trim())}
                  sx={{ ml: 2 }}
                  disabled={!selectedTag.trim()}
                >
                  Find Related
                </Button>
              </Box>
              
              {searchedTag && (
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Tags related to "{searchedTag}"
                    </Typography>
                    {isOfflineMode ? (
                      <Typography variant="body2" color="text.secondary">
                        Related tags analysis requires online access.
                        This feature will be available when you're back online.
                      </Typography>
                    ) : (
                      <RelatedTags tag={searchedTag} limit={10} />
                    )}
                  </CardContent>
                </Card>
              )}
            </Paper>
          </Grid>
          
          {/* Search Benefits */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h5" gutterBottom>
                Search Performance Improvements
              </Typography>
              <Typography variant="body1" paragraph>
                The search system has been enhanced with a dedicated tag table and indexing:
              </Typography>
              <Box component="ul" sx={{ pl: 4 }}>
                <Typography component="li" sx={{ mb: 1 }}>
                  <strong>Faster Searches:</strong> Dedicated indexes for tags provide nearly instant results
                </Typography>
                <Typography component="li" sx={{ mb: 1 }}>
                  <strong>Better Multi-word Searches:</strong> Each word in your search can match different tags
                </Typography>
                <Typography component="li" sx={{ mb: 1 }}>
                  <strong>More Accurate Results:</strong> Tags are properly separated and indexed for precise matching
                </Typography>
                <Typography component="li">
                  <strong>Tag Normalization:</strong> Tags are automatically cleaned and deduplicated
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default TagDashboard;
